import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "cn"

// CUSTOMIZED: `default` and `contrast` carry the app button design — Mix CTA
// uses cyan-300 / foreground (not semantic --primary cyan-500). Soft interactive
// chrome stays on --primary. `bg-clip-border` so the transparent border does not
// show page white around the fill. `contrast` is a black-outlined button that
// inverts to a solid black fill on hover/press. Both render disabled as a flat
// muted fill rather than the shared `disabled:opacity-50`, and keep the focus
// ring as their only focus cue. Link variant uses --primary.

// Do not rename or restyle `default` / `outline` / `ghost` without checking
// registry dependents (AlertDialog, Dialog, Sheet, Calendar, Carousel,
// Pagination, Sidebar, Combobox, InputGroup).
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-md border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // --- Registry contract (other shadcn primitives pin these) ---
        default: // default: AlertDialogAction, Sidebar
          "border-transparent bg-cyan-300 bg-clip-border text-foreground hover:bg-cyan-400 focus-visible:border-cyan-300 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100",

        outline: // outline: AlertDialog cancel, Dialog footer Close, Carousel, Pagination active
          "border-border bg-background shadow-xs hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        // ghost: Dialog/Sheet close, Calendar, Combobox, InputGroup, Sidebar, Pagination inactive
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",

        // --- App / brand (not pinned by registry primitives; safe to restyle) ---
        contrast: // contrast: brand high-contrast outline (temp secondary) — not stock outline
          "border-foreground text-foreground hover:bg-foreground hover:text-background active:bg-foreground active:text-background aria-expanded:bg-foreground aria-expanded:text-background focus-visible:border-foreground disabled:border-transparent disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100",

        secondary: // secondary: stock filled quiet action
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        destructive: // destructive: stock danger treatment
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: // link: text-as-button (modified)
          "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-9 gap-1.5 px-4 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),8px)] px-2 text-xs in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1 rounded-[min(var(--radius-md),10px)] px-2.5 in-data-[slot=button-group]:rounded-md has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5",
        lg: "h-10 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-9",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),8px)] in-data-[slot=button-group]:rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-md",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
